import MaterialIcon from '@/components/atom/MaterialIcon';
import { sanitizeHtml } from '@/entities/medicine-dictionary/util';
import { cn } from '@/utils/style';
import { Button } from 'doctornow-design-system';
import { useState } from 'react';
import AlertWarningTriangle from '../../../../public/images/medicine-dictionary/alertWarningTriangle.svg';

interface Props {
  simplifiedName?: string;
  sideEffects?: string[];
  interactionHtml?: string;
  cautions?: string[];
  medicinePictogramImages?: string[];
}

const MedicineGuideSideEffect = (props: Props) => {
  const [isCautionExpanded, setIsCautionExpanded] = useState(false);
  const shouldShowPictogramArea = props.medicinePictogramImages && props.medicinePictogramImages.length > 0;
  const shouldShowInteractionHtml = props.interactionHtml && props.interactionHtml.length > 0;
  const shouldShowButton = props.interactionHtml && props.interactionHtml.length > 0;

  const toggleCautionExpanded = () => {
    setIsCautionExpanded(!isCautionExpanded);
  };

  return (
    <section className={cn('flex flex-col gap-[20px] px-[24px] py-[28px]')}>
      <div className='flex items-center gap-[8px]'>
        <AlertWarningTriangle className='shrink-0' />
        <h2 className={cn('typography-ui-19sb shrink-1 select-text text-gray-700')}>
          {props.simplifiedName}, 부작용 및 주의사항
        </h2>
      </div>
      <div className='flex flex-col gap-[32px]'>
        <div className='flex flex-col gap-[8px]'>
          <h3 className='typography-ui-15m select-text text-gray-400'>부작용</h3>
          <div className='flex flex-col gap-[12px]'>
            <ul className='flex flex-col gap-[12px]'>
              <li className='typography-ui-15r select-text text-gray-700'>
                {props.sideEffects?.map((sideEffect) => sideEffect).join(', ')}
              </li>
            </ul>
          </div>
        </div>
        <div className='flex flex-col gap-[8px]'>
          <h3 className='typography-ui-15m select-text text-gray-400'>주의사항</h3>
          {props.cautions?.map((label, index) => {
            return (
              <div key={`caution-${label || index}`} className='flex flex-col gap-[12px]'>
                <ul className='flex flex-col gap-[12px]'>
                  <li
                    className={cn([
                      'typography-ui-15r select-text text-gray-700',
                      'relative pl-[20px]',
                      'before:absolute before:top-[10px] before:left-[6px] before:h-[4px] before:w-[4px] before:rounded-full before:bg-gray-700 before:content-[""]',
                      'flex gap-[8px]',
                    ])}
                    key={`caution-${label}`}
                  >
                    {label}
                  </li>
                </ul>
              </div>
            );
          })}
        </div>
      </div>
      {shouldShowPictogramArea && (
        <div className='flex gap-[12px] overflow-x-scroll md:overflow-x-hidden'>
          {props.medicinePictogramImages?.map((image, index) => {
            return (
              <img
                src={image}
                alt='medicine-pictogram'
                className='h-auto w-[54px] object-contain'
                key={`medicine-pictogram-${index}`}
              />
            );
          })}
        </div>
      )}

      {shouldShowInteractionHtml && isCautionExpanded && (
        <div
          className={cn([
            'mt-[20px] flex w-full flex-col',
            'typography-ui-15r select-text text-gray-700',
            'rounded-[12px] bg-gray-20 px-[20px] py-[16px]',
          ])}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(props.interactionHtml || ''),
          }}
        />
      )}

      {shouldShowButton && (
        <Button
          buttonStyle={'outlined'}
          buttonColor='gray'
          size='m'
          className={cn(['w-full transition-all duration-300 ease-in-out', 'hover:scale-[1.02] active:scale-[0.98]'])}
          type='button'
          onClick={toggleCautionExpanded}
        >
          <div className='flex items-center gap-2 transition-all duration-300 ease-in-out'>
            <span className='typography-ui-15sb text-gray-600'>{isCautionExpanded ? '접기' : '자세히 보기'}</span>
            <div
              className={cn([
                'transition-transform duration-300 ease-in-out',
                isCautionExpanded ? 'rotate-180' : 'rotate-0',
              ])}
            >
              <MaterialIcon name={'keyboard_arrow_down'} size={20} />
            </div>
          </div>
        </Button>
      )}
    </section>
  );
};

export default MedicineGuideSideEffect;
